import { createBrowserRouter } from 'react-router-dom'

import RouteError from '@/routes/RouteError'
import RouteFallback from '@/routes/RouteFallback'
import { navigateTo } from '@/routes/navigateTo'
import { routes } from '@/routes/routes'

/** Wraps a dynamic import so guards and layouts stay out of the entry chunk. */
const lazyComponent = (loader) => async () => ({ Component: (await loader()).default })

/**
 * Each `layout` in the route configuration resolves to a guard and, optionally,
 * a shell rendered around that branch's children.
 */
const LAYOUT_WRAPPERS = {
  dashboard: {
    guard: () => import('@/routes/ProtectedRoute'),
    layout: () => import('@/layouts/DashboardLayout'),
  },
  auth: {
    guard: () => import('@/routes/GuestRoute'),
    layout: null,
  },
}

/**
 * Maps one configured route onto a React Router object. Children recurse, so
 * nested routes need no special handling.
 */
const toRouteObject = (route) => {
  const object = route.index ? { index: true } : { path: route.path }

  if (route.redirectTo) object.Component = navigateTo(route.redirectTo)
  else if (route.element) object.lazy = route.element

  if (route.children) object.children = route.children.map(toRouteObject)

  // Metadata read by the sidebar, breadcrumbs and the document title.
  object.handle = {
    id: route.id,
    title: route.name,
    crumb: route.name && !route.hideCrumb ? () => route.name : undefined,
    // Resolves the crumb from the query cache once the record loads.
    dynamicCrumb: route.dynamicCrumb,
    access: route.access,
    // The role gate, for the pages that belong to a job rather than a grant.
    roles: route.roles,
  }

  return object
}

const toBranch = (branch) => {
  const { guard, layout } = LAYOUT_WRAPPERS[branch.layout]
  const children = branch.children.map(toRouteObject)

  return {
    lazy: lazyComponent(guard),
    ErrorBoundary: RouteError,
    HydrateFallback: RouteFallback,
    children: layout ? [{ lazy: lazyComponent(layout), children }] : children,
  }
}

/**
 * The router is a static, module-level object: access is enforced when a route
 * renders (see routes/ProtectedRoute.jsx), not by pruning the tree, so it does
 * not have to be rebuilt when the session — or a user's grants — change.
 */
export const buildRouter = () => createBrowserRouter(routes.map(toBranch))

export const router = buildRouter()
